/* Alpha Stretch Ceiling — shared UI
 * 1. Header turns solid after 60px of scroll (.is-scrolled)
 * 2. Scroll reveal: any .asc-reveal gets .is-in when it enters the viewport
 * 3. Mobile menu drawer, header height variable, back-to-top
 * 4. Announcement bar: rotates its messages; html.asc-scrolled lets it slide away
 * Both re-run when the Shopify theme editor reloads a section.
 */
(() => {
  const SCROLL_OFFSET = 60;

  /* ---------- header ---------- */
  const syncHeader = () => {
    const nav = document.querySelector('.asc-nav');
    const scrolled = window.scrollY > SCROLL_OFFSET;
    if (nav) nav.classList.toggle('is-scrolled', scrolled);
    document.documentElement.classList.toggle('asc-scrolled', scrolled);
  };

  /* ---------- announcement bar ---------- */
  let annTimer = null;
  const initAnnounce = () => {
    clearInterval(annTimer);
    const bar = document.querySelector('[data-asc-announce]');
    if (!bar) return;
    const items = [...bar.querySelectorAll('.asc-announce__item')];
    if (items.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let i = Math.max(0, items.findIndex((el) => el.classList.contains('is-active')));
    annTimer = setInterval(() => {
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, (parseInt(bar.dataset.interval, 10) || 5) * 1000);
  };
  window.addEventListener('scroll', syncHeader, { passive: true });

  /* ---------- reveal ---------- */
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -30px 0px' })
    : null;

  const observe = (root = document) => {
    root.querySelectorAll('.asc-reveal:not(.is-in)').forEach((el) => {
      if (io) io.observe(el);
      else el.classList.add('is-in');
    });
  };

  /* ---------- header height → --asc-nav-h (drawer + inner-page offset) ---------- */
  const measureNav = () => {
    const nav = document.querySelector('.asc-nav');
    if (!nav) return;
    // measure the compact (scrolled/solid) height, which is what inner pages and the open drawer show
    document.documentElement.style.setProperty('--asc-nav-h', `${Math.round(nav.getBoundingClientRect().height)}px`);
  };

  /* ---------- mobile drawer ---------- */
  const drawerState = (open) => {
    const nav = document.querySelector('.asc-nav');
    const drawer = document.querySelector('[data-asc-drawer]');
    const btn = document.querySelector('[data-asc-drawer-toggle]');
    if (!nav || !drawer || !btn) return;
    nav.classList.toggle('is-open', open);
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    btn.setAttribute('aria-expanded', String(open));
    const label = btn.querySelector('.asc-nav-toggle__label');
    if (label) label.textContent = open ? label.dataset.labelClose : label.dataset.labelOpen;
    drawer.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', open ? '0' : '-1'));
    document.documentElement.classList.toggle('asc-lock', open);
    if (open) measureNav();
  };
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-asc-drawer-toggle]');
    if (toggle) { drawerState(toggle.getAttribute('aria-expanded') !== 'true'); return; }
    if (e.target.closest('[data-asc-drawer-close]')) drawerState(false);
    const top = e.target.closest('[data-asc-top]');
    if (top) { e.preventDefault(); window.scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.querySelector('[data-asc-drawer].is-open')) {
      drawerState(false);
      document.querySelector('[data-asc-drawer-toggle]')?.focus();
    }
  });
  window.addEventListener('resize', () => {
    if (window.innerWidth > 980 && document.querySelector('[data-asc-drawer].is-open')) drawerState(false);
    measureNav();
  }, { passive: true });

  const init = () => { syncHeader(); observe(); measureNav(); initAnnounce(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* ---------- theme editor ---------- */
  document.addEventListener('shopify:section:load', (e) => { observe(e.target); syncHeader(); measureNav(); initAnnounce(); });
  document.addEventListener('shopify:section:unload', () => setTimeout(initAnnounce, 0));
  // a message picked in the editor is shown and the rotation pauses
  document.addEventListener('shopify:block:select', (e) => {
    if (!e.target.classList.contains('asc-announce__item')) return;
    clearInterval(annTimer);
    e.target.parentElement.querySelectorAll('.asc-announce__item').forEach((el) => el.classList.toggle('is-active', el === e.target));
  });
  // blocks selected in the editor should be visible right away
  document.addEventListener('shopify:block:select', (e) => {
    e.target.querySelectorAll('.asc-reveal').forEach((el) => el.classList.add('is-in'));
    if (e.target.classList.contains('asc-reveal')) e.target.classList.add('is-in');
  });
})();
